import React, { useMemo, useState } from "react";
import Layout from "../components/Layout";
import { useApp } from "../context/AppContext";
import {
  Plus,
  Edit,
  Trash2,
  Printer,
  ClipboardList,
  Search,
  ArrowLeftRight,
} from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import ProductForm from "../components/ProductForm";
import MovementForm from "../components/MovementForm";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { generateGiacenzaPDF, generateRiordinoPDF } from "../lib/pdf";

export default function Products() {
  const {
    products,
    createProduct,
    updateProduct,
    removeProduct,
  } = useApp();
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [openMov, setOpenMov] = useState(false);
  const [movProductId, setMovProductId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q) && !p.code.toLowerCase().includes(q)) {
        return false;
      }
      if (filter === "ok") return p.quantity > p.min_threshold;
      if (filter === "warn")
        return p.quantity > 0 && p.quantity <= p.min_threshold;
      if (filter === "danger") return p.quantity <= 0;
      return true;
    });
  }, [products, search, filter]);

  const openCreate = () => {
    setEditing(null);
    setOpenForm(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setOpenForm(true);
  };
  const onSubmit = async (payload) => {
    if (editing?.id) {
      const { code: _c, ...rest } = payload;
      await updateProduct(editing.id, rest);
    } else {
      await createProduct(payload);
    }
  };
  const confirmDelete = async () => {
    try {
      await removeProduct(deleteTarget.id);
      toast.success("Prodotto eliminato");
    } catch (e) {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeleteTarget(null);
    }
  };

  const openMovement = (productId) => {
    setMovProductId(productId);
    setOpenMov(true);
  };

  return (
    <Layout
      title="Prodotti"
      subtitle="Anagrafica reti da confezionamento molluschi"
    >
      <div className="panel mb-4">
        <div className="panel-header">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="relative max-w-sm w-full">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nome o codice..."
                className="pl-9"
                data-testid="products-search-input"
              />
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {[
                { k: "all", label: "Tutti" },
                { k: "ok", label: "OK" },
                { k: "warn", label: "Basso" },
                { k: "danger", label: "Esaurito" },
              ].map(({ k, label }) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded border transition-colors ${
                    filter === k
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`filter-${k}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => generateGiacenzaPDF(products)}
              disabled={products.length === 0}
              data-testid="print-giacenza-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Stampa giacenza
            </Button>
            <Button
              variant="outline"
              onClick={() => generateRiordinoPDF(products)}
              disabled={products.length === 0}
              data-testid="print-riordino-btn"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Lista riordino
            </Button>
            <Button
              onClick={openCreate}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo prodotto
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Prodotto</th>
                <th className="text-right">Giacenza</th>
                <th className="text-right">Soglia</th>
                <th>Stato</th>
                <th className="text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-slate-500 py-10">
                    {products.length === 0
                      ? "Nessun prodotto ancora. Crea il primo con 'Nuovo prodotto'."
                      : "Nessun prodotto corrisponde ai filtri."}
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`product-row-${p.code}`}
                  >
                    <td className="mono text-slate-600">{p.code}</td>
                    <td className="font-medium text-slate-900">{p.name}</td>
                    <td className="text-right mono font-semibold">
                      {p.quantity} <span className="text-slate-500 text-xs">{p.unit}</span>
                    </td>
                    <td className="text-right mono text-slate-500">
                      {p.min_threshold}
                    </td>
                    <td>
                      <StatusBadge
                        quantity={p.quantity}
                        threshold={p.min_threshold}
                      />
                    </td>
                    <td className="text-right">
                      <div className="inline-flex gap-1">
                        <button
                          title="Registra movimento"
                          onClick={() => openMovement(p.id)}
                          className="p-2 rounded hover:bg-slate-100 text-slate-600"
                          data-testid={`product-movement-${p.code}`}
                        >
                          <ArrowLeftRight className="w-4 h-4" />
                        </button>
                        <button
                          title="Modifica"
                          onClick={() => openEdit(p)}
                          className="p-2 rounded hover:bg-slate-100 text-slate-600"
                          data-testid={`product-edit-${p.code}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          title="Elimina"
                          onClick={() => setDeleteTarget(p)}
                          className="p-2 rounded hover:bg-red-50 text-red-600"
                          data-testid={`product-delete-${p.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ProductForm
        open={openForm}
        onOpenChange={setOpenForm}
        initial={editing}
        onSubmit={onSubmit}
      />
      <MovementForm
        open={openMov}
        onOpenChange={setOpenMov}
        defaultProductId={movProductId}
      />

      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="delete-product-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il prodotto?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <b>{deleteTarget?.name}</b> ({deleteTarget?.code}).
              Verranno rimossi anche tutti i movimenti associati. L'azione non è
              reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
